<script lang="ts">
  import { BarChart3, Calendar, Check, ChevronDown, TrendingDown, TrendingUp } from 'lucide-svelte';
  import type { LiveClosedTrade, LiveOpenTrade } from '@rhdashboard-vnext/shared';
  import { Button } from '$lib/components/ui/button';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
  } from '$lib/components/ui/dropdown-menu';
  import { Tooltip, TooltipContent, TooltipTrigger } from '$lib/components/ui/tooltip';
  import { computeSlippagePts } from '$lib/utils/slippage';
  import CopyableSessionId from './CopyableSessionId.svelte';
  import WeeklyBreakdownModal from './WeeklyBreakdownModal.svelte';

  // ============================================================
  // <SessionDetailPanel>
  //
  // Cuerpo expandido de una session: Strategy Configuration + Stats Grid +
  // Open Position + Trade List. Compartido entre SessionCard (Live/Developer
  // Monitor) y Sessions tab (expand row).
  //
  // Props desacoplados: cada caller arma el shape segun su fuente. Sessions
  // tab fetcha trades on-demand cuando el usuario expande una row; el Live
  // Monitor los recibe del endpoint /summary.
  // ============================================================

  type Theme = 'live' | 'developer' | 'sessions';

  interface Props {
    // Stats principales
    tradeCount: number;
    // pnl REAL (fill prices de Tradovate, incluye slippage). Es el numero
    // que el operador "cobro/perdio" en la realidad.
    totalPnL: number;
    // pnl BT (sin slippage, trigger prices) — opcional, si se pasa se muestra
    // stacked bajo el totalPnL real en el card "Total P&L". Lo provee el
    // backend en LiveSessionCard.totalBtPnL / SessionSummary.totalBtPnL.
    totalBtPnL?: number;
    profitFactor: number | null;
    winRate: number; // 0-100
    maxDrawdown: number; // <= 0

    // Config + extras
    config?: Record<string, unknown> | null;

    // Trades (cuando se conocen). Si null/undefined, la seccion NO se renderea.
    openTrades?: LiveOpenTrade[] | null;
    closedTrades?: LiveClosedTrade[] | null;

    // Theme accent. 'live' = naranja, 'developer' = purpura, 'sessions' = gold.
    theme?: Theme;
  }

  let {
    tradeCount,
    totalPnL,
    totalBtPnL,
    profitFactor,
    winRate,
    maxDrawdown,
    config = null,
    openTrades = null,
    closedTrades = null,
    theme = 'live',
  }: Props = $props();

  // ----- Weekly Breakdown modal -----
  let breakdownOpen = $state(false);

  function openBreakdown(e: MouseEvent): void {
    e.stopPropagation();
    breakdownOpen = true;
  }

  // ----- Helpers de config -----
  let cfg = $derived(config ?? {});

  function num(v: unknown): number | null {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    return null;
  }
  function str(v: unknown): string | null {
    if (typeof v === 'string' && v.length > 0) return v;
    return null;
  }
  function bool(v: unknown): boolean {
    return v === true;
  }

  let parametersFromId = $derived.by((): string | null => {
    return str(cfg.referenceSweepId) ?? str(cfg.originSweepId);
  });

  let hasMidCandleExit = $derived(bool(cfg.allowMidCandleExit));

  // ----- Formatters -----
  function fmtMoney(n: number | null): string {
    if (n == null) return '—';
    if (n === 0) return '$0.00';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  }

  function fmtPrice(n: number | null): string {
    if (n == null) return '—';
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function fmtPF(pf: number | null): string {
    if (tradeCount === 0) return '—';
    if (pf === null) return '∞';
    if (!Number.isFinite(pf)) return '—';
    return pf.toFixed(2);
  }

  function fmtDateTime(t: string | null): string {
    if (!t) return '—';
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  // Slippage en puntos. Convencion: '+X.XX pts' / '-X.XX pts' / '—'.
  // Sign mantiene la convencion universal de money (signo ANTES del valor).
  function fmtSlippage(n: number | null): string {
    if (n == null) return '—';
    if (n === 0) return '0.00 pts';
    const sign = n > 0 ? '+' : '-';
    return `${sign}${Math.abs(n).toFixed(2)} pts`;
  }

  // ----- Theme classes -----
  let primaryClass = $derived(
    theme === 'developer'
      ? 'text-cyan-300'
      : theme === 'sessions'
        ? 'text-gold'
        : 'text-primary',
  );
  let primaryBg = $derived(
    theme === 'developer'
      ? 'bg-cyan-500/10'
      : theme === 'sessions'
        ? 'bg-gold/10'
        : 'bg-primary/10',
  );

  // Primer open trade (si lo hay) para la sección "Open Position".
  let openPosition = $derived((openTrades && openTrades[0]) ?? null);

  // ¿Hay alguna config para mostrar? Si no, ocultamos el card de Strategy.
  let hasAnyConfig = $derived.by((): boolean => {
    if (!config) return false;
    return (
      str(cfg.timeSpan) != null ||
      num(cfg.atrPeriod) != null ||
      num(cfg.boxSizeMultiplier) != null ||
      num(cfg.minSameDirection) != null ||
      num(cfg.minReversalBlocks) != null ||
      num(cfg.volumeMin) != null ||
      num(cfg.slippage) != null ||
      num(cfg.offset) != null ||
      parametersFromId != null ||
      hasMidCandleExit
    );
  });

  // ============================================================
  // Group by day - filtro sobre el Trade List + recalc de KPIs.
  //
  // Day key: 'YYYY-MM-DD' del entryTime en ET wall-clock. entryTime (no
  // exitTime) porque sessions overnight como 18:00-00:01 pertenecen al
  // dia que arrancaron — un trade abierto el 12 a las 23:50 y cerrado el
  // 13 a las 00:15 es del "12" para el operador.
  //
  // Trade abierto (sin exitTime) cae naturalmente en su entryTime.
  // ============================================================

  let selectedDay = $state<string | null>(null);

  const DAY_KEY_FMT = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const DAY_LABEL_FMT = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });

  function dayKeyForTrade(t: { entryTime: string | null }): string | null {
    if (!t.entryTime) return null;
    const ms = Date.parse(t.entryTime);
    if (!Number.isFinite(ms)) return null;
    // 'en-CA' devuelve directamente 'YYYY-MM-DD'.
    return DAY_KEY_FMT.format(new Date(ms));
  }

  function dayLabelFromKey(key: string): string {
    // key viene como 'YYYY-MM-DD'. Parseamos como UTC mediodia para evitar
    // que el toLocaleString lo corra a otro dia por offset.
    const [y, m, d] = key.split('-').map(Number);
    if (!y || !m || !d) return key;
    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    return DAY_LABEL_FMT.format(dt);
  }

  // Dias disponibles en los closedTrades, desc (mas reciente arriba), con count.
  let availableDays = $derived.by((): Array<{ key: string; count: number }> => {
    if (!closedTrades || closedTrades.length === 0) return [];
    const counts = new Map<string, number>();
    for (const t of closedTrades) {
      const key = dayKeyForTrade(t);
      if (!key) continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.key.localeCompare(a.key));
  });

  // Si el dia seleccionado deja de existir (e.g. los trades cambiaron y ya
  // no hay trades de ese dia), reseteamos a All.
  $effect(() => {
    if (selectedDay && !availableDays.some((d) => d.key === selectedDay)) {
      selectedDay = null;
    }
  });

  // Trades del subset seleccionado (o todos si selectedDay === null).
  let filteredClosedTrades = $derived.by((): LiveClosedTrade[] => {
    if (!closedTrades) return [];
    if (selectedDay === null) return closedTrades;
    return closedTrades.filter((t) => dayKeyForTrade(t) === selectedDay);
  });

  // ----- KPIs displayed: si selectedDay es null usamos los props originales
  // (rollups de la session entera, ya calculados en backend). Si hay dia
  // seleccionado, recalculamos sobre filteredClosedTrades. -----
  let displayedTradeCount = $derived(
    selectedDay === null ? tradeCount : filteredClosedTrades.length,
  );

  let displayedTotalPnL = $derived.by((): number => {
    if (selectedDay === null) return totalPnL;
    let sum = 0;
    for (const t of filteredClosedTrades) sum += t.pnl;
    return sum;
  });

  // BT total: si hay selectedDay, sumamos btPnl de los trades del dia.
  // Si no, usamos el prop totalBtPnL del backend. Undefined cuando el caller
  // (Sessions tab) todavia no lo pasa -> no se muestra el stacked.
  let displayedTotalBtPnL = $derived.by((): number | undefined => {
    if (totalBtPnL === undefined) return undefined;
    if (selectedDay === null) return totalBtPnL;
    let sum = 0;
    for (const t of filteredClosedTrades) sum += t.btPnl;
    return sum;
  });

  let displayedProfitFactor = $derived.by((): number | null => {
    if (selectedDay === null) return profitFactor;
    let winSum = 0;
    let lossSum = 0;
    for (const t of filteredClosedTrades) {
      if (t.pnl >= 0) winSum += t.pnl;
      else lossSum += Math.abs(t.pnl);
    }
    if (lossSum === 0) return winSum > 0 ? null : 0; // null = infinito
    return winSum / lossSum;
  });

  let displayedWinRate = $derived.by((): number => {
    if (selectedDay === null) return winRate;
    if (filteredClosedTrades.length === 0) return 0;
    const wins = filteredClosedTrades.filter((t) => t.pnl >= 0).length;
    return (wins / filteredClosedTrades.length) * 100;
  });

  // Max drawdown peak-to-trough sobre la equity cumulativa del subset
  // (ordenado por entryTime ASC). Devuelve valor <= 0.
  let displayedMaxDrawdown = $derived.by((): number => {
    if (selectedDay === null) return maxDrawdown;
    if (filteredClosedTrades.length === 0) return 0;
    const sorted = [...filteredClosedTrades].sort((a, b) => {
      const ta = a.entryTime ? Date.parse(a.entryTime) : 0;
      const tb = b.entryTime ? Date.parse(b.entryTime) : 0;
      return ta - tb;
    });
    let cum = 0;
    let peak = 0;
    let maxDd = 0;
    for (const t of sorted) {
      cum += t.pnl;
      if (cum > peak) peak = cum;
      const dd = cum - peak;
      if (dd < maxDd) maxDd = dd;
    }
    return maxDd;
  });
</script>

<div class="border-t">
  <!-- Strategy Configuration -->
  {#if hasAnyConfig}
    <div class={['m-4 rounded-md border p-4', primaryBg].join(' ')}>
      <h5
        class={['mb-3 inline-flex items-center gap-2 text-sm font-semibold', primaryClass].join(
          ' ',
        )}
      >
        📊 Strategy Configuration
      </h5>

      <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4 lg:grid-cols-8">
        <div>
          <span class="text-muted-foreground">Timespan: </span>
          <span class="font-mono font-medium">{str(cfg.timeSpan) ?? 'N/A'}</span>
        </div>
        <div>
          <span class="text-muted-foreground">ATR Period: </span>
          <span class="font-mono font-medium">{num(cfg.atrPeriod) ?? 'N/A'}</span>
        </div>
        <div>
          <span class="text-muted-foreground">Box Size: </span>
          <span class="font-mono font-medium">{num(cfg.boxSizeMultiplier) ?? 'N/A'}</span>
        </div>
        <div>
          <span class="text-muted-foreground">Min Same Dir: </span>
          <span class="font-mono font-medium">{num(cfg.minSameDirection) ?? 'N/A'}</span>
        </div>
        <div>
          <span class="text-muted-foreground">Min Reversal: </span>
          <span class="font-mono font-medium">{num(cfg.minReversalBlocks) ?? 'N/A'}</span>
        </div>
        <div>
          <span class="text-muted-foreground">Volume Min: </span>
          <span class="font-mono font-medium">{num(cfg.volumeMin) ?? 'N/A'}</span>
        </div>
        <div>
          <span class="text-muted-foreground">Slippage: </span>
          <span class="font-mono font-medium">{num(cfg.slippage) ?? 0}</span>
        </div>
        <div>
          <span class="text-muted-foreground">Offset: </span>
          <span class="font-mono font-medium">{num(cfg.offset) ?? 0}</span>
        </div>
      </div>

      <!-- Parameters From: referenceSweepId / originSweepId / Conventional -->
      <div class="mt-3 flex flex-wrap items-center gap-1.5 border-t pt-3 text-sm">
        <span class="text-muted-foreground">Parameters From: </span>
        {#if parametersFromId}
          <CopyableSessionId sessionId={parametersFromId} ariaLabel="Copy sweep ID" />
          <button
            type="button"
            onclick={openBreakdown}
            class="border-gold/40 bg-gold/10 text-gold hover:bg-gold/20 hover:border-gold/60 inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium tracking-[-0.005em] transition-colors"
            title="View weekly breakdown of the origin sweep"
            aria-label="Open weekly breakdown"
          >
            <BarChart3 class="size-3" />
            Weekly
          </button>
        {:else}
          <span class="font-medium">Conventional</span>
        {/if}
      </div>

      <!-- Mid-candle exit params (gold) -->
      {#if hasMidCandleExit}
        <div
          class="border-primary/20 mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t pt-3 text-sm sm:grid-cols-4"
        >
          <div>
            <span class="text-muted-foreground">Stop Loss: </span>
            <span class="text-gold font-mono font-medium"
              >{num(cfg.stopLossPoints) ?? 0} pts</span
            >
          </div>
          <div>
            <span class="text-muted-foreground">Take Profit: </span>
            <span class="text-gold font-mono font-medium"
              >{num(cfg.takeProfitPoints) ?? 0} pts</span
            >
          </div>
          <div>
            <span class="text-muted-foreground">Trail Activate: </span>
            <span class="text-gold font-mono font-medium"
              >{num(cfg.trailActivatePoints) ?? 0} pts</span
            >
          </div>
          <div>
            <span class="text-muted-foreground">Trail Stop: </span>
            <span class="text-gold font-mono font-medium"
              >{num(cfg.trailingStopPercent) ?? 0}%</span
            >
          </div>
        </div>
      {/if}
    </div>
  {/if}

  <!-- Stats grid (5 cards) - usa displayed* que respeta selectedDay -->
  <div class="m-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
    <div class="bg-muted/30 rounded-md border p-3">
      <div class="text-muted-foreground text-[10px] uppercase tracking-wider">
        {selectedDay === null ? 'Total Trades' : 'Trades (day)'}
      </div>
      <div class="font-mono text-2xl font-semibold tabular-nums">{displayedTradeCount}</div>
    </div>
    <div class="bg-muted/30 rounded-md border p-3">
      <div class="text-muted-foreground text-[10px] uppercase tracking-wider">
        {selectedDay === null ? 'Total P&L' : 'P&L (day)'}
      </div>
      <div
        class={[
          'font-mono text-2xl font-semibold tabular-nums',
          displayedTotalPnL > 0 && 'text-success',
          displayedTotalPnL < 0 && 'text-destructive',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {fmtMoney(displayedTotalPnL)}
      </div>
      {#if displayedTotalBtPnL !== undefined && displayedTradeCount > 0}
        <div
          class="text-muted-foreground/70 mt-0.5 font-mono text-[11px] tabular-nums"
          title="BT P&L (sin slippage, trigger prices)"
        >
          BT {fmtMoney(displayedTotalBtPnL)}
        </div>
      {/if}
    </div>
    <div class="bg-muted/30 rounded-md border p-3">
      <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Profit Factor</div>
      <div class="font-mono text-2xl font-semibold tabular-nums">
        {#if displayedTradeCount === 0}
          —
        {:else if displayedProfitFactor === null}
          ∞
        {:else if !Number.isFinite(displayedProfitFactor)}
          —
        {:else}
          {displayedProfitFactor.toFixed(2)}
        {/if}
      </div>
    </div>
    <div class="bg-muted/30 rounded-md border p-3">
      <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Win Rate</div>
      <div class="font-mono text-2xl font-semibold tabular-nums">
        {displayedTradeCount > 0 ? `${displayedWinRate.toFixed(1)}%` : '—'}
      </div>
    </div>
    <div class="bg-muted/30 rounded-md border p-3">
      <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Max Drawdown</div>
      <div class="text-destructive font-mono text-2xl font-semibold tabular-nums">
        {fmtMoney(displayedMaxDrawdown)}
      </div>
    </div>
  </div>

  <!-- Open Position section -->
  {#if openPosition}
    <div class="m-4">
      <h5
        class={['mb-3 inline-flex items-center gap-2 text-sm font-semibold', primaryClass].join(
          ' ',
        )}
      >
        📍 Open Position
      </h5>
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Side</div>
          <div
            class={[
              'flex items-center gap-1 font-mono text-xl font-semibold uppercase tabular-nums',
              openPosition.side === 'long' ? 'text-success' : 'text-destructive',
            ].join(' ')}
          >
            {#if openPosition.side === 'long'}
              <TrendingUp class="size-4" />
            {:else}
              <TrendingDown class="size-4" />
            {/if}
            {openPosition.side ?? '—'}
          </div>
        </div>
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Entry Price</div>
          <div class="font-mono text-xl font-semibold tabular-nums">
            {fmtPrice(openPosition.entryPrice)}
          </div>
        </div>
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Quantity</div>
          <div class="font-mono text-xl font-semibold tabular-nums">
            {openPosition.quantity ?? '—'}
          </div>
        </div>
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Stop Loss</div>
          <div class="font-mono text-xl font-semibold tabular-nums">
            {fmtPrice(openPosition.stopLoss)}
          </div>
        </div>
      </div>
    </div>
  {/if}

  <!-- Trade List section -->
  {#if closedTrades && closedTrades.length > 0}
    <div class="m-4">
      <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h5
          class={['inline-flex items-center gap-2 text-sm font-semibold', primaryClass].join(' ')}
        >
          📋 Trade List ({filteredClosedTrades.length}{closedTrades.length === 50 &&
          selectedDay === null
            ? '+'
            : ''}{selectedDay !== null && filteredClosedTrades.length !== closedTrades.length
            ? ` of ${closedTrades.length}`
            : ''})
        </h5>

        {#if availableDays.length > 1}
          <!-- Stop propagation en el wrapper para evitar que el click llegue
               al header padre clickeable (SessionCard) que toggleria expand. -->
          <div onclick={(e) => e.stopPropagation()} onkeydown={(e) => e.stopPropagation()} role="presentation">
            <DropdownMenu>
              <DropdownMenuTrigger>
                {#snippet child({ props })}
                  <Button {...props} variant="outline" size="sm" class="h-8 gap-1.5">
                    <Calendar class="size-3.5" />
                    <span class="text-muted-foreground">Group by day:</span>
                    <span class="font-medium">
                      {selectedDay === null ? 'All' : dayLabelFromKey(selectedDay)}
                    </span>
                    <ChevronDown class="text-muted-foreground size-3.5" />
                  </Button>
                {/snippet}
              </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-56">
              <DropdownMenuLabel>Group by day</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onclick={() => {
                  selectedDay = null;
                }}
              >
                <span class="flex-1">All</span>
                <span class="text-muted-foreground mr-2 text-xs tabular-nums">
                  {closedTrades.length}
                </span>
                {#if selectedDay === null}
                  <Check class="size-3.5" />
                {/if}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {#each availableDays as d (d.key)}
                <DropdownMenuItem
                  onclick={() => {
                    selectedDay = d.key;
                  }}
                >
                  <span class="flex-1">{dayLabelFromKey(d.key)}</span>
                  <span class="text-muted-foreground mr-2 text-xs tabular-nums">{d.count}</span>
                  {#if selectedDay === d.key}
                    <Check class="size-3.5" />
                  {/if}
                </DropdownMenuItem>
              {/each}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        {/if}
      </div>
      <div class="overflow-x-auto rounded-md border">
        <table class="w-full text-xs">
          <thead class="bg-muted/40 text-muted-foreground">
            <tr>
              <th class="px-3 py-2 text-left font-normal uppercase tracking-wider">Entry Time</th>
              <th class="px-3 py-2 text-left font-normal uppercase tracking-wider">Exit Time</th>
              <th class="px-3 py-2 text-left font-normal uppercase tracking-wider">Side</th>
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider">Entry</th>
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider">
                Trad. Entry
              </th>
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider">Exit</th>
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider">
                Trad. Exit
              </th>
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider">
                <Tooltip>
                  <TooltipTrigger>
                    {#snippet child({ props })}
                      <span
                        {...props}
                        class="cursor-help underline decoration-dotted underline-offset-2"
                      >
                        Slippage
                      </span>
                    {/snippet}
                  </TooltipTrigger>
                  <TooltipContent class="max-w-xs font-sans">
                    Diferencia en puntos entre el precio teórico del engine y el
                    fill real de Tradovate (entry + exit). Positivo = favorable.
                  </TooltipContent>
                </Tooltip>
              </th>
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider">P&L</th>
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider">
                <Tooltip>
                  <TooltipTrigger>
                    {#snippet child({ props })}
                      <span
                        {...props}
                        class="cursor-help underline decoration-dotted underline-offset-2"
                      >
                        BT P&L
                      </span>
                    {/snippet}
                  </TooltipTrigger>
                  <TooltipContent class="max-w-xs font-sans">
                    P&L "modelo" sin slippage: usa los trigger prices del engine
                    (close de vela/Renko). Compará con P&L real para ver cuánto
                    aportó el slippage. Fórmula: (exitTrigger - entryTrigger) ×
                    qty × $/pt.
                  </TooltipContent>
                </Tooltip>
              </th>
              <th class="px-3 py-2 text-left font-normal uppercase tracking-wider">Reason</th>
            </tr>
          </thead>
          <tbody>
            {#each filteredClosedTrades as t, i (`${t.entryTime ?? ''}-${i}`)}
              {@const slippage = computeSlippagePts({
                side: t.side,
                entryTriggerPrice: t.entryTriggerPrice,
                entryPrice: t.entryPrice,
                exitTriggerPrice: t.exitTriggerPrice,
                exitPrice: t.exitPrice,
              })}
              <tr
                class={[
                  'border-t transition-colors',
                  t.pnl >= 0 ? 'hover:bg-success/5' : 'hover:bg-destructive/5',
                ].join(' ')}
              >
                <td class="px-3 py-1.5 font-mono tabular-nums">{fmtDateTime(t.entryTime)}</td>
                <td class="px-3 py-1.5 font-mono tabular-nums">{fmtDateTime(t.exitTime)}</td>
                <td
                  class={[
                    'px-3 py-1.5 font-mono uppercase',
                    t.side === 'long' ? 'text-success' : 'text-destructive',
                  ].join(' ')}
                >
                  {#if t.side === 'long'}
                    <TrendingUp class="inline size-3" />
                  {:else if t.side === 'short'}
                    <TrendingDown class="inline size-3" />
                  {/if}
                  {t.side ?? '—'}
                </td>
                <td class="px-3 py-1.5 text-right font-mono tabular-nums">
                  {fmtPrice(t.entryTriggerPrice)}
                </td>
                <td class="text-muted-foreground px-3 py-1.5 text-right font-mono tabular-nums">
                  {fmtPrice(t.entryPrice)}
                </td>
                <td class="px-3 py-1.5 text-right font-mono tabular-nums">
                  {fmtPrice(t.exitTriggerPrice)}
                </td>
                <td class="text-muted-foreground px-3 py-1.5 text-right font-mono tabular-nums">
                  {fmtPrice(t.exitPrice)}
                </td>
                <td
                  class={[
                    'px-3 py-1.5 text-right font-mono tabular-nums',
                    slippage != null && slippage > 0 && 'text-success',
                    slippage != null && slippage < 0 && 'text-destructive',
                    slippage == null && 'text-muted-foreground',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {fmtSlippage(slippage)}
                </td>
                <td
                  class={[
                    'px-3 py-1.5 text-right font-mono font-semibold tabular-nums',
                    t.pnl > 0 && 'text-success',
                    t.pnl < 0 && 'text-destructive',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {fmtMoney(t.pnl)}
                </td>
                <td
                  class={[
                    'px-3 py-1.5 text-right font-mono tabular-nums',
                    t.btPnl > 0 && 'text-success/70',
                    t.btPnl < 0 && 'text-destructive/70',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {fmtMoney(t.btPnl)}
                </td>
                <td class="text-muted-foreground px-3 py-1.5 text-[11px]">
                  {t.closeReason ?? '—'}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>
  {/if}
</div>

<!-- Weekly Breakdown modal (lazy: solo monta cuando se abre por primera vez) -->
{#if parametersFromId}
  <WeeklyBreakdownModal
    sweepId={parametersFromId}
    bind:open={breakdownOpen}
    onClose={() => {
      breakdownOpen = false;
    }}
  />
{/if}
